import { useCallback, useEffect, useMemo, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Link, Typography } from '@mui/material';
import { DatePicker, Table } from 'antd';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import AppButton from '../../../../components/ui/AppButton';
import { useGetTransaction } from '../../../../hooks/useTransaction';

const { RangePicker } = DatePicker;

const formatPoint = (value) => {
  if (value === null || value === undefined || value === '') return '-';

  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '-';

  return numberValue.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getRowKey = (row, index) => row.tableKey || row._id || `${row.create_timestamp}-${index}`;

const canExpandRow = (row) => Boolean(row.shipping_id?.length);

const getMainColumns = ({ expandedRowKeys, onToggleExpand }) => [
  {
    title: 'Point',
    dataIndex: 'point',
    key: 'point',
    width: 160,
    align: 'center',
    sorter: true,
    render: (value, row) => {
      const pointText = formatPoint(value);
      if (pointText === '-') return '-';
      const isIncrease = row.type === 'Increase';
      const isExpandable = canExpandRow(row);
      const isExpanded = expandedRowKeys.includes(row.tableKey);
      const ExpandIcon = isExpanded ? ExpandLessIcon : ExpandMoreIcon;

      return (
        <Box
          sx={{
            alignItems: 'center',
            color: isIncrease ? '#5cb85c' : '#FD0004',
            cursor: isExpandable ? 'pointer' : 'default',
            display: 'inline-flex',
            fontWeight: 700,
            gap: 0.5,
            justifyContent: 'center',
          }}
        >
          {isExpandable && <ExpandIcon sx={{ color: '#002B38', fontSize: '22px' }} />}
          {isIncrease ? '+' : '-'}
          {pointText}
        </Box>
      );
    },
    onCell: (row) => ({
      onClick: () => onToggleExpand(row),
      style: { cursor: canExpandRow(row) ? 'pointer' : 'default' },
    }),
  },
  {
    title: 'Detail',
    dataIndex: 'detail',
    key: 'detail',
    width: 220,
    render: (value) => value || 'แลกรางวัล',
  },
  {
    title: 'Date Time',
    dataIndex: 'create_timestamp',
    key: 'create_timestamp',
    width: 190,
    align: 'center',
    sorter: true,
    render: (value) => (value ? dayjs(value).format('DD/MM/YYYY HH:mm:ss') : '-'),
  },
];

const getRewardPoint = (reward) => {
  if (
    reward.point !== null &&
    reward.point !== undefined &&
    reward.quantity !== null &&
    reward.quantity !== undefined
  ) {
    return Number(reward.point) * Number(reward.quantity);
  }
  return reward.point;
};

const expandedColumns = [
  {
    title: 'Order ID',
    dataIndex: 'order_id',
    key: 'order_id',
    width: 150,
    render: (value) => value || '-',
  },
  {
    title: 'Tracking',
    key: 'tracking',
    width: 160,
    render: (_, row) => {
      if (row.delivery && row.tracking_url) {
        return (
          <Link
            href={row.tracking_url}
            sx={{ color: '#005972', fontWeight: 700, textDecoration: 'underline' }}
            target="_blank"
          >
            {row.delivery}
          </Link>
        );
      }

      return row.delivery || '-';
    },
  },
  {
    title: 'Reward',
    dataIndex: 'reward_name',
    key: 'reward_name',
    width: 260,
    render: (value) => value || '-',
  },
  {
    title: 'Qty',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 90,
    align: 'center',
    render: (value) => (value ?? '-'),
  },
  {
    title: 'Point',
    dataIndex: 'point',
    key: 'point',
    width: 140,
    align: 'right',
    render: (value) => {
      const pointText = formatPoint(value);
      if (pointText === '-') return '-';

      return (
        <Typography component="span" sx={{ color: '#FD0004', fontWeight: 700 }}>
          -{pointText}
        </Typography>
      );
    },
  },
];

const getExpandedRows = (shippingList = []) =>
  shippingList.flatMap((shipping, shippingIndex) => {
    const rewards = shipping.list_reward || [];
    const baseRow = {
      delivery: shipping.delivery,
      order_id: shipping.order_id,
      tracking_url: shipping.tracking_url,
    };

    if (!rewards.length) {
      return [
        {
          ...baseRow,
          key: `${shipping._id || shipping.order_id || 'shipping'}-${shippingIndex}`,
          point: undefined,
          quantity: undefined,
          reward_name: undefined,
        },
      ];
    }

    return rewards.map((reward, rewardIndex) => ({
      ...baseRow,
      key: `${shipping._id || shipping.order_id || 'shipping'}-${reward._id || reward.sku || rewardIndex}`,
      point: getRewardPoint(reward),
      quantity: reward.quantity,
      reward_name: reward.name,
    }));
  });

const ExpandedTransactionDetails = ({ shippingList = [] }) => (
  <Box
    sx={{
      p: { xs: 1, md: 1.5 },
      '& .ant-table-cell': {
        padding: '12px 14px !important',
      },
      '& .ant-table-thead > tr > th': {
        padding: '10px 14px !important',
      },
    }}
  >
    <Table
      bordered
      columns={expandedColumns}
      dataSource={getExpandedRows(shippingList)}
      pagination={false}
      rowKey="key"
      scroll={{ x: 'max-content' }}
      size="small"
      locale={{ emptyText: 'ไม่พบรายการรางวัล' }}
    />
  </Box>
);

const HistoryTable = ({ data, sx = {} }) => {
  const companyId = data?._id;
  const [rangeDate, setRangeDate] = useState([dayjs().subtract(1, 'year'), dayjs()]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    company_id: undefined,
    start_date: undefined,
    end_date: undefined,
    sort_col: 'create_timestamp',
    sort_value: false,
  });

  const { data: transactionData, isLoading: transactionLoading } = useGetTransaction({
    ...query,
    company_id: companyId,
    enabled: Boolean(companyId),
  });

  const transactionList = useMemo(() => {
    return (
      transactionData?.Data?.data?.map((item, index) => ({
        ...item,
        tableKey: getRowKey(item, index),
      })) || []
    );
  }, [transactionData]);

  const handleToggleExpand = useCallback((row) => {
    if (!canExpandRow(row)) return;

    setExpandedRowKeys((prev) =>
      prev.includes(row.tableKey) ? prev.filter((key) => key !== row.tableKey) : [...prev, row.tableKey]
    );
  }, []);

  const mainColumns = useMemo(
    () => getMainColumns({ expandedRowKeys, onToggleExpand: handleToggleExpand }),
    [expandedRowKeys, handleToggleExpand]
  );

  const handleSearchHistory = () => {
    const startDate = rangeDate[0] ? dayjs(rangeDate[0]).format('YYYY-MM-DD') : undefined;
    const endDate = rangeDate[1] ? dayjs(rangeDate[1]).format('YYYY-MM-DD') : undefined;
    setQuery((prev) => ({ ...prev, page: 1, start_date: startDate, end_date: endDate }));
  };

  useEffect(() => {
    setQuery((prev) => ({ ...prev, company_id: companyId }));
    setExpandedRowKeys([]);
  }, [companyId]);

  return (
    <Box sx={{ padding: '10px 0 60px', ...sx }}>
      <Box
        py={3}
        display="flex"
        justifyContent="space-between"
        gap={2}
        sx={{ position: 'relative' }}
      >
        <RangePicker
          locale={locale}
          format="DD-MM-YYYY"
          className="myDateRange"
          value={rangeDate}
          onCalendarChange={(value) => setRangeDate(value)}
          getPopupContainer={(trigger) => trigger.parentElement}
          style={{
            width: '100%',
            maxWidth: '350px',
            height: '50px',
            fontSize: '1.25rem',
            lineHeight: '1rem',
          }}
        />
        <AppButton variant="dangerStrong" onClick={handleSearchHistory}>
          ค้นหา
        </AppButton>
      </Box>
      <Box
        sx={{
          padding: '5px',
          backgroundColor: 'white',
          borderRadius: '15px',
          border: '2px solid white',
          '& .ant-pagination': {
            alignItems: 'center',
          },
          '& .ant-pagination-options': {
            alignSelf: 'center',
          },
          '& .ant-table-expanded-row > .ant-table-cell': {
            backgroundColor: '#F6FAFB',
            padding: { xs: '10px', md: '12px 16px 16px' },
          },
        }}
      >
        <Table
          bordered
          rowKey="tableKey"
          columns={mainColumns}
          dataSource={transactionList}
          loading={transactionLoading}
          expandable={{
            expandedRowKeys,
            expandedRowRender: (record) => (
              <ExpandedTransactionDetails shippingList={record.shipping_id || []} />
            ),
            rowExpandable: (record) => Boolean(record.shipping_id?.length),
            showExpandColumn: false,
            onExpandedRowsChange: setExpandedRowKeys,
          }}
          pagination={{
            current: query.page,
            pageSize: query.limit,
            total: transactionData?.Data.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '50', '100', '500'],
            hideOnSinglePage: true,
          }}
          onChange={(pagination, filters, sorter) => {
            const sortField = sorter.field || sorter.columnKey || 'create_timestamp';
            setQuery((prev) => ({
              ...prev,
              page: pagination.current,
              limit: pagination.pageSize,
              sort_col: ['point', 'create_timestamp'].includes(sortField)
                ? sortField
                : 'create_timestamp',
              sort_value: sorter.order === 'ascend',
            }));
          }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'ไม่พบประวัติการใช้งาน' }}
        />
      </Box>
    </Box>
  );
};

export default HistoryTable;
